/**
 * Submission creation form (forwardRef component exposing handleSubmitClick
 * via SubmissionCreateRef). type "normal" is embedded in BountyViewSubmit
 * ("/bounties/:bountyId") and submitted from the parent; type "ai" renders its
 * own submit button in the admin SubmissionVerificationAdminPage.
 * Offers AI pre-fill of title/description/price/images from a pasted product
 * URL (SubmissionVerificationAPI.extractProductInfoForUrl). On submit it
 * creates the submission (BountySubmissionAPI.createSubmission or
 * AdminAPI.createAiSubmission), verifies it with SubmissionVerificationAPI,
 * then AI-validates/rejects and uploads image URLs to S3 for normal flow.
 */
import { useUserState } from "@/contexts/UserContext";
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import InputField from "@/components/InputField";
import { submissionDescriptionMessage, submissionTitleMessage } from "@/constants/submission";
import { FieldConfig } from "@/components/InputField";
import BountySubmissionAPI from "@/api/bountySubmission";
import LoadingSpinner from "@/components/LoadingSpinner";
import { BUTTON_SIZE, FIELD_WIDTH } from "@/constants/ui";
import { useError } from "@/contexts/ErrorContext";
import { AdminAPI } from "@/api/admin";
import {
    SUBMISSION_DESCRIPTION_MAX_LENGTH,
    SUBMISSION_TITLE_MAX_LENGTH,
    SubmissionVerificationResult,
} from "@/types/submission";
import ImageUrlsField from "@/components/ImageUrlsField";
import { SubmissionVerificationAPI } from "@/api/submissionVerification";
import {
    processSubmissionVerificationResultFailure,
    submissionVerificationResultIsValid,
} from "@/utils/submission";
import { ArrowDownToLine, X } from "lucide-react";

export type SubmissionCreateType = "normal" | "ai";

export const MAX_IMAGE_URLS = 10;
export interface SubmissionCreateRef {
    handleSubmitClick: () => Promise<void>;
}

interface SubmissionCreateProps {
    type: SubmissionCreateType;
    bountyId?: number;
    onCancel?: () => void;
    onValidationChange?: (isValid: boolean) => void;
    onSuccess?: () => void;
}

const SubmissionCreatePage = forwardRef<SubmissionCreateRef, SubmissionCreateProps>(
    ({ bountyId, onCancel, onValidationChange, onSuccess, type }, ref) => {
        const userState = useUserState();
        const [title, setTitle] = useState<string>("");
        const [description, setDescription] = useState<string>("");
        const [productUrl, setProductUrl] = useState<string>("");
        const [price, setPrice] = useState<number | null>(null);
        const [imageUrls, setImageUrls] = useState<string[]>([]);
        const { showError } = useError();
        const [loading, setLoading] = useState<boolean>(false);
        const [showSuccess, setShowSuccess] = useState<boolean>(false);
        const [showInitialOverlay, setShowInitialOverlay] = useState<boolean>(true);
        const [overlayUrl, setOverlayUrl] = useState<string>("");
        const [showExtractTooltip, setShowExtractTooltip] = useState<boolean>(false);

        const titleConfig: FieldConfig = {
            key: "title",
            label: "title",
            type: "text",
            placeholder: submissionTitleMessage,
            readonly: false,
            readonlyValue: null,
            fieldState: title,
            setFieldState: setTitle,
        };

        const descriptionConfig: FieldConfig = {
            key: "description",
            label: "description",
            type: "textarea",
            placeholder: submissionDescriptionMessage,
            readonly: false,
            readonlyValue: null,
            fieldState: description,
            setFieldState: setDescription,
        };

        const priceConfig: FieldConfig = {
            key: "price",
            label: "price",
            type: "number",
            placeholder: "the price of the item",
            readonly: false,
            readonlyValue: null,
            fieldState: price,
            setFieldState: setPrice,
            min: 1,
            max: 100000,
            useTwoDecimalPlaces: true,
        };

        const productUrlConfig: FieldConfig = {
            key: "productUrl",
            label: "item url",
            type: "text",
            placeholder: "paste the link to purchase the item",
            readonly: false,
            readonlyValue: null,
            fieldState: productUrl,
            setFieldState: setProductUrl,
            pasteOnly: true,
        };

        const overlayUrlConfig: FieldConfig = {
            key: "overlayUrl",
            label: "product url",
            type: "text",
            placeholder: "paste url here",
            readonly: false,
            readonlyValue: null,
            fieldState: overlayUrl,
            setFieldState: setOverlayUrl,
            pasteOnly: true,
        };

        const addImageUrl = () => {
            if (imageUrls.length < MAX_IMAGE_URLS) {
                setImageUrls([...imageUrls, ""]);
            }
        };

        const updateImageUrl = (index: number, value: string) => {
            const newImageUrls = [...imageUrls];
            newImageUrls[index] = value;
            setImageUrls(newImageUrls);
        };

        const removeImageUrl = (index: number) => {
            const newImageUrls = imageUrls.filter((_, i) => i !== index);
            setImageUrls(newImageUrls.length === 0 ? [""] : newImageUrls);
        };

        useImperativeHandle(ref, () => ({
            handleSubmitClick,
        }));

        const allFieldsAreFilled = () => {
            if (!title || !description || !productUrl || !price) {
                return false;
            }
            // Check if at least one image URL is provided
            const validImageUrls = imageUrls.filter((url) => url.trim() !== "");
            if (validImageUrls.length === 0) {
                return false;
            }
            return true;
        };

        function checkIfAllInputValid(): boolean {
            const errors: string[] = [];

            if (title.trim().length < 5) {
                errors.push("title must be at least 5 characters");
            }
            if (description.trim().length < 15) {
                errors.push(
                    "description must be at least 15 characters, it's important to be detailed"
                );
            }
            if (price! <= 0) {
                errors.push("price must be greater than 0");
            }

            // Check if at least one image URL is provided
            const validImageUrls = imageUrls.filter((url) => url.trim() !== "");
            if (validImageUrls.length === 0) {
                errors.push("at least one image URL is required");
            }

            if (errors.length > 0) {
                const errorMessage = errors.join("\n");
                console.log(errorMessage);
                showError(errorMessage);
                return false;
            }

            return true;
        }

        // This is the main submit button handler called from the parent component
        const handleSubmitClick = async () => {
            let result: SubmissionVerificationResult | void;

            if (type === "ai") {
                result = await handleAiSubmitClick();
            } else {
                result = await handleNormalSubmitClick();
            }

            if (result) {
                if (!submissionVerificationResultIsValid(result)) {
                    showError(processSubmissionVerificationResultFailure(result));
                } else {
                    // Both valid and available - show success state
                    setShowSuccess(true);
                }
            }
        };

        const createSubmissionAndVerify = async () => {
            const filteredImageUrls = imageUrls.filter((url) => url.trim() !== "");
            const { submission } = await BountySubmissionAPI.createSubmission(bountyId!, {
                title: title,
                description: description,
                productUrl: productUrl,
                price: price!,
                imageUrls: filteredImageUrls,
                porsuiUserId: userState!.id,
                bountyId: bountyId!,
            });
            const result: SubmissionVerificationResult =
                await SubmissionVerificationAPI.verifySubmission(
                    {
                        title: title,
                        description: description,
                        price: String(price),
                        imageUrls: filteredImageUrls,
                        available: true,
                        isProductPage: true,
                        productUrl: productUrl,
                    },
                    "create"
                );
            if (submissionVerificationResultIsValid(result)) {
                await BountySubmissionAPI.aiValidateSubmission(bountyId!, submission.id!);
                BountySubmissionAPI.uploadImageUrlsToS3(bountyId!, submission.id!);
            } else {
                await BountySubmissionAPI.aiRejectSubmission(bountyId!, submission.id!);
            }
            return result;
        };

        const createAiSubmissionAndVerify = async () => {
            const filteredImageUrls = imageUrls.filter((url) => url.trim() !== "");
            await AdminAPI.createAiSubmission({
                title: title,
                description: description,
                productUrl: productUrl,
                price: price!,
                imageUrls: filteredImageUrls,
            });
            const result: SubmissionVerificationResult =
                await SubmissionVerificationAPI.verifySubmission(
                    {
                        title: title,
                        description: description,
                        price: String(price),
                        imageUrls: filteredImageUrls,
                        available: true,
                        isProductPage: true,
                        productUrl: productUrl,
                    },
                    "create"
                );
            // Don't need to upload image urls to S3 for ai submissions
            return result;
        };

        const handleNormalSubmitClick = async (): Promise<SubmissionVerificationResult | void> => {
            if (!bountyId) {
                showError("Bounty ID is required for this submission type.");
                return;
            }
            if (!checkIfAllInputValid()) {
                return;
            }
            setLoading(true);
            try {
                const result = await createSubmissionAndVerify();
                return result;
            } catch (error) {
                showError(`failed to create submission`);
            } finally {
                setLoading(false);
            }
        };

        const handleAiSubmitClick = async (): Promise<SubmissionVerificationResult | void> => {
            if (!checkIfAllInputValid()) {
                return;
            }
            setLoading(true);
            try {
                const result = await createAiSubmissionAndVerify();
                return result;
            } catch (error) {
                showError(`failed to create submission`);
            } finally {
                setLoading(false);
            }
        };

        const handleExtractProductInfoClick = async () => {
            setLoading(true);
            try {
                const { extractedData } =
                    await SubmissionVerificationAPI.extractProductInfoForUrl(productUrl);
                setTitle(extractedData.title || "");
                setDescription(extractedData.description || "");
                setPrice(extractedData.price ? parseFloat(extractedData.price) : null);
                setImageUrls(extractedData.imageUrls || []);
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : "";
                showError(
                    `Failed to extract product information${
                        errorMessage ? `: ${errorMessage}` : ""
                    }`
                );
            } finally {
                setLoading(false);
            }
        };

        const handleInitialUrlSubmit = async () => {
            if (!overlayUrl.trim()) {
                showError("Please enter a valid URL");
                return;
            }
            setLoading(true);
            try {
                const { extractedData } =
                    await SubmissionVerificationAPI.extractProductInfoForUrl(overlayUrl);
                setProductUrl(overlayUrl);
                setTitle(extractedData.title || "");
                setDescription(extractedData.description || "");
                setPrice(extractedData.price ? parseFloat(extractedData.price) : null);
                setImageUrls(extractedData.imageUrls || []);
                setShowInitialOverlay(false);
            } catch (error) {
                showError("Failed to extract product information");
            } finally {
                setLoading(false);
            }
        };

        const handleDismissOverlay = () => {
            setShowInitialOverlay(false);
        };

        const handleCancelClick = () => {
            setTitle("");
            setDescription("");
            setProductUrl("");
            setImageUrls([]);
            onCancel?.();
        };

        useEffect(() => {
            const isValid = allFieldsAreFilled();
            onValidationChange?.(isValid);
        }, [title, description, productUrl, price, imageUrls, onValidationChange]);

        const prevValidationError = useRef<string | null>(null);

        useEffect(() => {
            const timer = setTimeout(() => {
                const errors: string[] = [];

                if (title.length > 0 && title.trim().length < 5) {
                    errors.push("title must be at least 5 characters");
                }
                if (title.length > SUBMISSION_TITLE_MAX_LENGTH) {
                    errors.push(`title must be at most ${SUBMISSION_TITLE_MAX_LENGTH} characters`);
                }
                if (description.length > 0 && description.trim().length < 15) {
                    errors.push("description must be at least 15 characters");
                }
                if (description.length > SUBMISSION_DESCRIPTION_MAX_LENGTH) {
                    errors.push(
                        `description must be at most ${SUBMISSION_DESCRIPTION_MAX_LENGTH} characters`
                    );
                }
                if (price !== null && price <= 0) {
                    errors.push("price must be greater than 0");
                }

                const errorMessage = errors.length > 0 ? errors.join("\n") : null;

                if (errorMessage && errorMessage !== prevValidationError.current) {
                    showError(errorMessage);
                }
                prevValidationError.current = errorMessage;
            }, 1000);

            return () => clearTimeout(timer);
        }, [title, description, price]);

        return (
            <div className="relative">
                {/* Initial URL Entry Overlay */}
                {showInitialOverlay && (
                    <div className="fixed inset-0 bg-black/80 z-50 flex flex-col items-center justify-center overflow-hidden">
                        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative">
                            <button
                                onClick={handleDismissOverlay}
                                className="absolute top-4 right-4 text-slate-400 hover:text-slate-950 transition-colors"
                                disabled={loading}
                            >
                                <X size={20} />
                            </button>
                            <p className="text-sm text-slate-600 my-4 text-center font-porsui">
                                paste a link to the product you want to submit and we'll use AI to
                                pull the product info and fill out the form for you. You can
                                edit/add more details after that, but they'll be verified by our AI
                                system with the product information.
                            </p>
                            <div
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && overlayUrl.trim()) {
                                        handleInitialUrlSubmit();
                                    }
                                }}
                            >
                                <InputField fieldConfig={overlayUrlConfig} className="mb-4" />
                            </div>
                            <button
                                onClick={handleInitialUrlSubmit}
                                disabled={loading || !overlayUrl.trim()}
                                className={`w-full py-3 rounded-lg font-porsui text-white transition-colors ${
                                    loading
                                        ? "bg-gray-300 cursor-not-allowed"
                                        : "bg-slate-950 hover:bg-slate-800"
                                }`}
                            >
                                {loading ? "extracting..." : "pre-fill product info"}
                            </button>
                        </div>
                    </div>
                )}

                {/* Loading overlay */}
                {(loading || showSuccess) && !showInitialOverlay && (
                    <div className="fixed inset-0 bg-slate-50/80 z-40 flex flex-col items-center justify-center overflow-hidden">
                        <LoadingSpinner
                            success={showSuccess}
                            message={showSuccess ? undefined : "validating submission with ai"}
                            successMessage="the submission was successfully submitted"
                            onSuccessComplete={() => {
                                setShowSuccess(false);
                                onSuccess?.();
                            }}
                        />
                    </div>
                )}

                <div className="w-full bg-transparent flex flex-col items-center md:flex-row md:items-start gap-4 md:ml-8">
                    {/* Image URLs Field - Left side */}
                    <div className="w-full md:w-96 flex-shrink-1 px-8 md:px-0">
                        <ImageUrlsField
                            imageUrls={imageUrls}
                            onUpdateImageUrl={updateImageUrl}
                            onAddImageUrl={addImageUrl}
                            onRemoveImageUrl={removeImageUrl}
                            readonly={false}
                            maxUrls={MAX_IMAGE_URLS}
                        />
                    </div>

                    {/* Input Fields - Right side */}
                    <div className={`${FIELD_WIDTH} h-full flex flex-col justify-between`}>
                        {/* Title Field */}
                        <InputField
                            key="title"
                            fieldConfig={titleConfig}
                            characterLimit={SUBMISSION_TITLE_MAX_LENGTH}
                            className="mb-4"
                        />
                        {/* Description Text Area */}
                        <InputField
                            key="description"
                            fieldConfig={descriptionConfig}
                            characterLimit={SUBMISSION_DESCRIPTION_MAX_LENGTH}
                            showCharacterCount
                            className="mb-4 flex-grow"
                            height="h-full min-h-[176px]"
                        />
                        {/* Price */}
                        <InputField
                            key="price"
                            fieldConfig={priceConfig}
                            characterLimit={10}
                            className="mb-4"
                        />
                        {/* Item URL */}
                        <div className="flex flex-row items-end justify-between mb-4">
                            <InputField key="productUrl" fieldConfig={productUrlConfig} />
                            <div className="relative">
                                <button
                                    className={`bg-slate-20 rounded-lg p-2 ml-1 ${
                                        loading || !productUrl.trim()
                                            ? "opacity-50 cursor-not-allowed"
                                            : ""
                                    }`}
                                    onClick={handleExtractProductInfoClick}
                                    disabled={loading || !productUrl.trim()}
                                    onMouseEnter={() => setShowExtractTooltip(true)}
                                    onMouseLeave={() => setShowExtractTooltip(false)}
                                >
                                    <ArrowDownToLine size={BUTTON_SIZE} />
                                </button>
                                {showExtractTooltip && (
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-slate-950 text-white text-xs rounded shadow-lg z-50 whitespace-normal text-center font-porsui">
                                        this will automatically extract the data when you paste a
                                        link here
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {type === "normal" && (
                    <div className="flex flex-col w-full text-center items-center p-2 justify-center">
                        <button
                            className={`py-2 text-black font-porsui text-center`}
                            onClick={handleCancelClick}
                            disabled={loading}
                        >
                            cancel
                        </button>
                    </div>
                )}
                {type === "ai" && (
                    <div className="flex flex-col w-full text-center items-center p-2 justify-center">
                        <button
                            className={`py-2 text-black font-porsui text-center`}
                            onClick={handleAiSubmitClick}
                            disabled={loading}
                        >
                            submit
                        </button>
                    </div>
                )}
            </div>
        );
    }
);

export default SubmissionCreatePage;
